import React, { useState, useEffect, useRef } from 'react';
import { saveSessionLearning, getSessionLearning, markSessionLevelResult } from '../../services/storage';
import { speak, speakAlert, startListening, stopListening, detectSaveCommand, isMuted, toggleMute } from '../../services/voiceService';
import { apiUrl } from '../../services/apiBase';

// ── Styles ────────────────────────────────────────────────────────────────────
const C = {
  bg:        'rgba(7, 9, 26, 0.93)',
  border:    'rgba(0, 212, 255, 0.18)',
  blue:      '#00d4ff',
  green:     '#00ff88',
  red:       '#f87171',
  gold:      '#ffd700',
  text:      '#e2e8f0',
  muted:     '#94a3b8',
  dim:       'rgba(148,163,184,0.6)',
};

const base = {
  fontFamily: "'Courier New', monospace",
  fontSize:   '12px',
  color:      C.text,
};

// ── Components ────────────────────────────────────────────────────────────────
function Section({ title, children, accent = C.blue, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <div style={{ marginBottom: '6px', border: `1px solid ${accent}22`, borderRadius: '8px', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ padding: '5px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `${accent}0d` }}>
        <span style={{ fontSize: '9px', fontWeight: '700', color: accent, letterSpacing: '1.5px' }}>{title}</span>
        <span style={{ fontSize: '10px', color: C.muted }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding: '8px 10px' }}>{children}</div>}
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ color: C.dim, fontSize: '11px' }}>{label}</span>
      <span style={{ color: color || C.text, fontSize: '11px', fontWeight: '600' }}>{value || '—'}</span>
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 75 ? C.green : score >= 50 ? C.gold : C.red;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
        <span style={{ fontSize: '10px', color: C.dim }}>Setup Score</span>
        <span style={{ fontSize: '12px', fontWeight: '700', color }}>{score}/100</span>
      </div>
      <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px' }}>
        <div style={{ width: `${score}%`, height: '100%', borderRadius: '2px', background: color, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
}

// ── Main Overlay Window ───────────────────────────────────────────────────────
export default function OverlayWindow() {
  const [analysis,    setAnalysis]    = useState(null);
  const [sessionLog,  setSessionLog]  = useState([]);
  const [chatOpen,    setChatOpen]    = useState(false);
  const [chatInput,   setChatInput]   = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [listening,   setListening]   = useState(false);
  const [micText,     setMicText]     = useState('');
  const [muted,       setMutedState]  = useState(isMuted());
  const [minimized,   setMinimized]   = useState(false);
  const [width,       setWidth]       = useState(320);
  const [dragging,    setDragging]    = useState(false);
  const [dragStart,   setDragStart]   = useState({ x: 0, y: 0 });
  const [pos,         setPos]         = useState({ x: 0, y: 0 });
  const chatEndRef = useRef(null);
  const isElectron = !!window.electronAPI?.isElectron;

  // Subscribe to analysis from main window
  useEffect(() => {
    if (!isElectron) return;
    const unsub = window.electronAPI.onAnalysis((data) => {
      setAnalysis(data);
      setSessionLog(getSessionLearning());
      if (data.alert) speakAlert(data.alert);
    });
    return unsub;
  }, [isElectron]);

  useEffect(() => { setSessionLog(getSessionLearning()); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatHistory]);

  // Window controls
  const handleClose    = () => { if (isElectron) window.electronAPI.closeOverlay(); };
  const handleMinimize = () => {
    if (isElectron) window.electronAPI.minimizeOverlay();
    setMinimized(true);
  };
  const handleMaximize = () => {
    const newW = width === 320 ? 480 : 320;
    setWidth(newW);
    if (isElectron) window.electronAPI.setOverlaySize(newW, 700);
    setMinimized(false);
  };

  // Drag header
  const onDragStart = (e) => {
    setDragging(true);
    setDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };
  const onDragMove = (e) => {
    if (!dragging) return;
    setPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onDragEnd = () => setDragging(false);

  // Chat send
  const handleChat = async (text) => {
    if (!text.trim()) return;
    const msg = text.trim();
    setChatInput('');
    setChatHistory(h => [...h, { role: 'user', content: msg }]);

    if (detectSaveCommand(msg)) {
      handleSaveRule();
      return;
    }

    try {
      const res = await fetch(apiUrl('/api/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: `You are JARVIS overlay assistant for XAU/USD trading. Be very concise (2-3 sentences max). Current chart context: ${analysis ? JSON.stringify({ symbol: analysis.symbol, price: analysis.price, trend: analysis.trend, bias: analysis.bias }) : 'No analysis yet'}`,
          messages: [...chatHistory, { role: 'user', content: msg }],
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || 'Sir, I could not process that.';
      setChatHistory(h => [...h, { role: 'assistant', content: reply }]);
      speak(reply, { rate: 0.92 });
    } catch {
      setChatHistory(h => [...h, { role: 'assistant', content: 'Sir, connection error.' }]);
    }
  };

  // Mic
  const handleMic = () => {
    if (listening) { stopListening(); setListening(false); return; }
    const ok = startListening((text) => {
      setListening(false);
      setMicText(text);
      setTimeout(() => setMicText(''), 3000);
      if (detectSaveCommand(text)) handleSaveRule();
      else handleChat(text);
    });
    if (ok) { setListening(true); setChatOpen(true); }
  };

  // Save rule
  const handleSaveRule = () => {
    if (!analysis) return;
    const ctx = `${analysis.symbol} ${analysis.timeframe} — ${analysis.bias}: ${analysis.key_observation}`;
    saveSessionLearning({ type: 'rule', title: `Rule: ${analysis.symbol}`, content: ctx, saved_as_rule: true });
    setSessionLog(getSessionLearning());
    speak('Rule saved to JARVIS brain, Sir.');
  };

  const handleMarkLevel = (id, result) => {
    markSessionLevelResult(id, result);
    setSessionLog(getSessionLearning());
    speak(result === 'held' ? 'Level held noted Sir.' : 'Level rejected, learning updated Sir.');
  };

  const handleToggleMute = () => { const m = toggleMute(); setMutedState(m); };

  const trendColor = (t) => t === 'BULLISH' ? C.green : t === 'BEARISH' ? C.red : C.gold;
  const biasColor  = (b) => b === 'BUY' ? C.green : b === 'SELL' ? C.red : C.muted;

  return (
    <div
      onMouseMove={onDragMove} onMouseUp={onDragEnd}
      style={{ position: 'fixed', inset: 0, background: 'transparent', userSelect: dragging ? 'none' : 'auto' }}>

      <div style={{
        ...base,
        position: 'absolute',
        top:    pos.y + 'px',
        left:   pos.x + 'px',
        width:  width + 'px',
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: '14px',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        overflow: 'hidden',
        transition: minimized ? 'none' : 'width 0.2s',
      }}>

        {/* ── Header (draggable) ─────────────────────────────────────────────── */}
        <div
          onMouseDown={onDragStart}
          style={{
            padding: '8px 12px',
            background: 'rgba(0,212,255,0.08)',
            borderBottom: `1px solid ${C.border}`,
            cursor: 'grab',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px' }}>🤖</span>
            <div>
              <div style={{ fontSize: '10px', fontWeight: '800', color: C.blue, letterSpacing: '2px' }}>JARVIS</div>
              <div style={{ fontSize: '8px', color: C.muted }}>Live Overlay</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={handleToggleMute} title="Mute" style={winBtn(muted ? '#444' : C.blue)}>
              {muted ? '🔇' : '🔊'}
            </button>
            <button onClick={() => setChatOpen(o => !o)} title="Chat" style={winBtn(chatOpen ? C.gold : C.blue)}>
              💬
            </button>
            <button onClick={handleMic} title="Mic" style={winBtn(listening ? C.red : C.blue)}>
              {listening ? '🔴' : '🎤'}
            </button>
            <button onClick={handleMinimize} title="Minimize" style={winBtn(C.muted)}>─</button>
            <button onClick={handleMaximize} title="Expand" style={winBtn(C.muted)}>⤢</button>
            <button onClick={handleClose}    title="Close"    style={winBtn(C.red)}>✕</button>
          </div>
        </div>

        {!minimized && (
          <div style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto', padding: '8px' }}>

            {/* Mic feedback */}
            {micText && (
              <div style={{ background: 'rgba(0,212,255,0.08)', borderRadius: '6px', padding: '6px 10px', marginBottom: '6px', fontSize: '11px', color: C.blue }}>
                🎤 "{micText}"
              </div>
            )}

            {/* ── 1. Price & Bias ─────────────────────────────────────────── */}
            {analysis ? (
              <>
                <Section title="📍 PRICE & MARKET BIAS" accent={C.blue}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '8px' }}>
                    <div style={infoBox}>
                      <div style={{ fontSize: '9px', color: C.muted }}>PRICE</div>
                      <div style={{ fontSize: '15px', fontWeight: '800', color: C.blue }}>${analysis.price}</div>
                    </div>
                    <div style={infoBox}>
                      <div style={{ fontSize: '9px', color: C.muted }}>BIAS</div>
                      <div style={{ fontSize: '15px', fontWeight: '800', color: biasColor(analysis.bias) }}>{analysis.bias}</div>
                    </div>
                  </div>
                  <Row label="Symbol"    value={analysis.symbol}    />
                  <Row label="Timeframe" value={analysis.timeframe} />
                  <Row label="Trend"     value={analysis.trend}     color={trendColor(analysis.trend)} />
                  <Row label="Structure" value={analysis.structure} />
                </Section>

                {/* ── 2. TRADE PLAN ───────────────────────────────────────── */}
                {analysis.trade_plan && (
                  <div style={{
                    marginBottom: '6px', borderRadius: '8px', overflow: 'hidden',
                    border: analysis.trade_plan.action === 'WAIT' ? `1px solid ${C.gold}44`
                          : analysis.trade_plan.action === 'BUY'  ? `1px solid ${C.green}55`
                          : `1px solid ${C.red}55`,
                  }}>
                    {/* Action */}
                    <div style={{
                      padding: '7px 10px',
                      background: analysis.trade_plan.action === 'WAIT' ? `${C.gold}15`
                                : analysis.trade_plan.action === 'BUY'  ? `${C.green}12`
                                : `${C.red}12`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '14px', fontWeight: '900', letterSpacing: '1px',
                        color: analysis.trade_plan.action === 'WAIT' ? C.gold
                             : analysis.trade_plan.action === 'BUY'  ? C.green : C.red,
                      }}>
                        {analysis.trade_plan.action === 'BUY' ? '▲ BUY' : analysis.trade_plan.action === 'SELL' ? '▼ SELL' : '⏸ WAIT'}
                      </span>
                      <span style={{ fontSize: '11px', color: C.gold, fontWeight: '700' }}>
                        {analysis.grade && `Grade ${analysis.grade}`} {analysis.trade_plan.rr && `· ${analysis.trade_plan.rr}`}
                      </span>
                    </div>
                    {/* Levels */}
                    {analysis.trade_plan.action !== 'WAIT' && (
                      <div style={{ padding: '6px 10px' }}>
                        {[
                          ['Entry',  analysis.trade_plan.entry, C.blue],
                          ['SL',     analysis.trade_plan.sl,    C.red],
                          ['TP1',    analysis.trade_plan.tp1,   C.green],
                          ['TP2',    analysis.trade_plan.tp2,   C.gold],
                        ].map(([l, v, col]) => v && (
                          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <span style={{ fontSize: '10px', color: C.muted }}>{l}</span>
                            <span style={{ fontSize: '13px', fontWeight: '800', color: col }}>${v}</span>
                          </div>
                        ))}
                        {analysis.trade_plan.reason && (
                          <div style={{ fontSize: '10px', color: C.muted, marginTop: '5px', fontStyle: 'italic', lineHeight: '1.4' }}>
                            {analysis.trade_plan.reason}
                          </div>
                        )}
                      </div>
                    )}
                    {analysis.trade_plan.action === 'WAIT' && analysis.trade_plan.reason && (
                      <div style={{ padding: '6px 10px', fontSize: '10px', color: C.gold }}>{analysis.trade_plan.reason}</div>
                    )}
                  </div>
                )}

                {/* ── 3. AI Analysis ──────────────────────────────────────── */}
                <Section title="🧠 AI ANALYSIS" accent={C.blue}>
                  {analysis.key_observation && (
                    <div style={{ fontSize: '11px', color: C.text, lineHeight: '1.6', marginBottom: '8px', padding: '6px', background: 'rgba(0,212,255,0.05)', borderRadius: '6px' }}>
                      {analysis.key_observation}
                    </div>
                  )}
                  <ScoreBar score={analysis.setup_score || 0} />
                  {analysis.candle_pattern !== 'None' && analysis.candle_pattern && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: C.gold }}>
                      🕯 {analysis.candle_pattern}
                    </div>
                  )}
                  {analysis.rules_applied?.length > 0 && (
                    <div style={{ marginTop: '6px' }}>
                      {analysis.rules_applied.map((r, i) => (
                        <div key={i} style={{ fontSize: '10px', color: C.green, padding: '1px 0' }}>✓ {r}</div>
                      ))}
                    </div>
                  )}
                </Section>

                {/* ── 4. Alert ────────────────────────────────────────────── */}
                {analysis.alert && (
                  <div style={{ background: 'rgba(255,215,0,0.08)', border: `1px solid ${C.gold}33`, borderRadius: '8px', padding: '8px 10px', marginBottom: '6px', fontSize: '11px', color: C.gold }}>
                    ⚠️ {analysis.alert}
                  </div>
                )}

                {/* ── 4. Drawn Levels ─────────────────────────────────────── */}
                {analysis.drawn_levels?.length > 0 && (
                  <Section title="✏️ YOUR DRAWN LEVELS" accent="#a78bfa">
                    {analysis.drawn_levels.map((lv, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ fontSize: '11px', color: lv.type === 'resistance' ? C.red : lv.type === 'trendline' ? C.gold : C.green }}>
                          ━ ${lv.price}
                        </span>
                        <span style={{ fontSize: '10px', color: C.muted }}>{lv.type}{lv.label ? ` · ${lv.label}` : ''}</span>
                      </div>
                    ))}
                  </Section>
                )}

                {/* ── 5. Fibonacci ────────────────────────────────────────── */}
                {analysis.fibonacci?.length > 0 && (
                  <Section title="📐 FIBONACCI LEVELS" accent={C.gold}>
                    {analysis.fibonacci.map((f, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ fontSize: '11px', color: f.zone === 'strong' ? C.gold : C.muted }}>
                          {f.level} {f.zone === 'strong' ? '⭐' : ''}
                        </span>
                        <span style={{ fontSize: '11px', color: C.text }}>${f.price}</span>
                      </div>
                    ))}
                  </Section>
                )}

                {/* ── 6. Liquidity ────────────────────────────────────────── */}
                {analysis.liquidity && analysis.liquidity !== 'None visible' && (
                  <Section title="💧 LIQUIDITY" accent={C.red}>
                    <div style={{ fontSize: '11px', color: C.text }}>{analysis.liquidity}</div>
                  </Section>
                )}

                {/* ── 7. Save Rule ─────────────────────────────────────────── */}
                <button onClick={handleSaveRule} style={{
                  width: '100%', padding: '7px', borderRadius: '7px', fontSize: '11px', fontWeight: '600',
                  background: 'rgba(0,255,136,0.1)', border: `1px solid ${C.green}33`, color: C.green, cursor: 'pointer', marginBottom: '6px',
                }}>
                  💾 Save Analysis as Rule
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 10px', color: C.muted }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📡</div>
                <div style={{ fontSize: '11px' }}>Waiting for analysis...</div>
                <div style={{ fontSize: '10px', marginTop: '4px', color: C.dim }}>Start Live Monitor in main app</div>
              </div>
            )}

            {/* ── 8. Session Log ──────────────────────────────────────────── */}
            {sessionLog.length > 0 && (
              <Section title={`📖 TODAY'S LEARNING (${sessionLog.length})`} accent="#a78bfa" collapsed>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {[...sessionLog].reverse().slice(0, 10).map((item) => (
                    <div key={item.id} style={{ padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ fontSize: '11px', color: C.text, fontWeight: '600' }}>{item.title}</div>
                      {item.content && <div style={{ fontSize: '10px', color: C.muted, marginTop: '1px' }}>{item.content.slice(0, 80)}</div>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <span style={{ fontSize: '9px', color: C.dim }}>{item.time}</span>
                        {item.price && !item.result && (
                          <div style={{ display: 'flex', gap: '3px' }}>
                            <button onClick={() => handleMarkLevel(item.id, 'held')}
                              style={{ ...smallBtn(C.green), padding: '2px 7px', fontSize: '9px' }}>Held</button>
                            <button onClick={() => handleMarkLevel(item.id, 'rejected')}
                              style={{ ...smallBtn(C.red), padding: '2px 7px', fontSize: '9px' }}>Rejected</button>
                          </div>
                        )}
                        {item.result && (
                          <span style={{ fontSize: '10px', color: item.result === 'held' ? C.green : C.red }}>
                            {item.result === 'held' ? '✅' : '❌'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* ── 9. Chat Panel ────────────────────────────────────────────── */}
            {chatOpen && (
              <Section title="💬 CHAT WITH JARVIS" accent={C.gold}>
                <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '6px' }}>
                  {chatHistory.length === 0 && (
                    <div style={{ fontSize: '10px', color: C.dim, textAlign: 'center', padding: '10px' }}>
                      Ask me anything about the chart, Sir.
                    </div>
                  )}
                  {chatHistory.map((m, i) => (
                    <div key={i} style={{
                      padding: '5px 8px', borderRadius: '6px', marginBottom: '4px', fontSize: '11px', lineHeight: '1.5',
                      background: m.role === 'user' ? 'rgba(0,212,255,0.08)' : 'rgba(0,255,136,0.05)',
                      color: m.role === 'user' ? C.blue : C.text,
                    }}>
                      <span style={{ fontSize: '9px', color: C.dim }}>{m.role === 'user' ? 'You' : 'JARVIS'}: </span>
                      {m.content}
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleChat(chatInput)}
                    placeholder="Ask JARVIS..."
                    style={{
                      flex: 1, background: 'rgba(0,0,0,0.4)', border: `1px solid ${C.border}`,
                      borderRadius: '6px', padding: '6px 8px', color: C.text, fontSize: '11px', outline: 'none',
                    }}
                  />
                  <button onClick={() => handleChat(chatInput)}
                    style={{ ...smallBtn(C.blue), padding: '6px 10px' }}>➤</button>
                  <button onClick={handleMic}
                    style={{ ...smallBtn(listening ? C.red : C.blue), padding: '6px 10px' }}>
                    {listening ? '🔴' : '🎤'}
                  </button>
                </div>
              </Section>
            )}
          </div>
        )}

        {minimized && (
          <div style={{ padding: '6px 12px', cursor: 'pointer', textAlign: 'center', fontSize: '10px', color: C.muted }}
            onClick={() => setMinimized(false)}>
            Click to restore overlay ▲
          </div>
        )}
      </div>
    </div>
  );
}

// Style helpers
const winBtn = (color) => ({
  width: '22px', height: '22px', borderRadius: '50%', border: 'none', cursor: 'pointer',
  background: `${color}22`, color, fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 0,
});

const smallBtn = (color) => ({
  padding: '3px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: '600', cursor: 'pointer',
  background: `${color}15`, border: `1px solid ${color}33`, color,
});

const infoBox = {
  background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px 10px', textAlign: 'center',
};
